import React from 'react';

const LandingPage = ({ onStartAssessment }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900">
      {/* Background decorations */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute top-40 right-10 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Navigation */}
      <nav className="relative z-10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <svg className="w-8 h-8 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span className="text-xl font-bold text-white">OCD-CAT</span>
          </div>
          <div className="hidden md:flex space-x-8">
            <a href="#features" className="text-gray-300 hover:text-white transition-colors">Features</a>
            <a href="#how-it-works" className="text-gray-300 hover:text-white transition-colors">How It Works</a>
            <a href="#about" className="text-gray-300 hover:text-white transition-colors">About</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24">
          <div className="text-center">
            <div className="inline-block mb-6">
              <span className="px-4 py-2 bg-teal-500/20 border border-teal-500/30 rounded-full text-teal-300 text-sm font-medium">
                Research-Backed Assessment Tool
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              OCD Symptom<br />
              <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                Adaptive Assessment
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10">
              OCD-CAT uses advanced Item Response Theory and computerized adaptive testing to efficiently
              measure OCD symptom severity with precision and clinical reliability.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onStartAssessment}
                className="px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-semibold rounded-xl hover:from-teal-600 hover:to-cyan-700 transition-all transform hover:scale-105 shadow-lg shadow-teal-500/30"
              >
                Start Assessment
              </button>
              <a
                href="#how-it-works"
                className="px-8 py-4 bg-white/10 border border-white/20 text-white font-semibold rounded-xl hover:bg-white/20 transition-all"
              >
                Learn More
              </a>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">4-44</div>
              <div className="text-gray-400">Adaptive Items</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">~5min</div>
              <div className="text-gray-400">Average Duration</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">IRT</div>
              <div className="text-gray-400">Based Scoring</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">PDF</div>
              <div className="text-gray-400">Report Generation</div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <section id="features" className="py-24 bg-black/20">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
              Advanced Assessment Features
            </h2>
            <p className="text-gray-400 text-center max-w-2xl mx-auto mb-16">
              Powered by psychometric science and modern technology
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-6">
                  <svg className="w-6 h-6 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Adaptive Item Selection</h3>
                <p className="text-gray-400">
                  Uses Kullback-Leibler divergence to select the most informative questions based on your responses,
                  reducing test length while maintaining accuracy.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-6">
                  <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Precision Scoring</h3>
                <p className="text-gray-400">
                  Expected A Posteriori (EAP) estimation provides reliable theta scores with confidence intervals,
                  using the Graded Response Model for nuanced measurement.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-6">
                  <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Clinical Reports</h3>
                <p className="text-gray-400">
                  Generate detailed PDF reports with severity classification, percentile rankings,
                  and evidence-based clinical recommendations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-24">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
              How It Works
            </h2>
            <p className="text-gray-400 text-center max-w-2xl mx-auto mb-16">
              A streamlined assessment process designed for accuracy and efficiency
            </p>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">1</div>
                <h3 className="text-lg font-semibold text-white mb-2">Start Session</h3>
                <p className="text-gray-400 text-sm">Begin your assessment with a single click. No registration required.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">2</div>
                <h3 className="text-lg font-semibold text-white mb-2">Answer Questions</h3>
                <p className="text-gray-400 text-sm">Respond to adaptively selected items about OCD symptoms and experiences.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">3</div>
                <h3 className="text-lg font-semibold text-white mb-2">Get Results</h3>
                <p className="text-gray-400 text-sm">View your severity level, percentile rank, and detailed visualizations.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">4</div>
                <h3 className="text-lg font-semibold text-white mb-2">Download Report</h3>
                <p className="text-gray-400 text-sm">Generate a comprehensive PDF report for your records or clinician.</p>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-24 bg-black/20">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
              About OCD-CAT
            </h2>
            <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">
              Understanding the science behind the assessment
            </p>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-12">
              <div className="prose prose-invert max-w-none">
                <p className="text-gray-300 mb-6">
                  <strong className="text-white">OCD-CAT (Obsessive-Compulsive Disorder Computerized Adaptive Test)</strong> is a
                  psychometrically validated assessment tool that uses Item Response Theory (IRT) to measure OCD
                  symptom severity efficiently and accurately.
                </p>
                <p className="text-gray-300 mb-6">
                  Unlike traditional fixed-length questionnaires, OCD-CAT adapts to each individual by selecting
                  questions that provide the most information about their specific level of OCD symptomatology. This
                  approach typically requires fewer items while maintaining or improving measurement precision.
                </p>
                <p className="text-gray-300 mb-6">
                  The assessment uses a <strong className="text-white">Graded Response Model (GRM)</strong> to handle
                  polytomous response data, and employs <strong className="text-white">Kullback-Leibler divergence</strong>
                  for optimal item selection. Ability estimation is performed using <strong className="text-white">Expected
                  A Posteriori (EAP)</strong> methods with carefully calibrated prior distributions.
                </p>
                <div className="mt-8 p-6 bg-teal-500/10 border border-teal-500/20 rounded-xl">
                  <p className="text-teal-200 text-sm">
                    <strong>Note:</strong> This tool is designed for research and educational purposes.
                    It is not intended to replace professional clinical assessment or diagnosis. If you
                    are experiencing significant distress, please consult a qualified mental health professional.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Begin?
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto mb-10">
              Start your adaptive assessment now. The test typically takes 3-7 minutes to complete.
            </p>
            <button
              onClick={onStartAssessment}
              className="inline-block px-10 py-5 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-semibold text-lg rounded-xl hover:from-teal-600 hover:to-cyan-700 transition-all transform hover:scale-105 shadow-xl shadow-teal-500/30"
            >
              Start Assessment
            </button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <svg className="w-6 h-6 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="text-white font-semibold">OCD-CAT</span>
            </div>
            <p className="text-gray-500 text-sm">
              Obsessive-Compulsive Disorder Computerized Adaptive Test
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
